import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Download, Package, Wallet, Scale, TrendingUp } from 'lucide-react';
import { relatorioEnvios } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';

const BRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
const hojeISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Fortaleza' });
const somaDias = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const brData = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

const PERIODOS = [
  ['7', 'Últimos 7 dias'], ['30', 'Últimos 30 dias'], ['90', 'Últimos 90 dias'], ['mes', 'Este mês'], ['custom', 'Personalizado'],
];

const Cartao = ({ icon: Icon, titulo, valor, sub }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
    <div className="flex items-center gap-2 text-slate-500 text-sm"><Icon className="w-4 h-4" /> {titulo}</div>
    <div className="mt-2 text-2xl font-extrabold text-slate-900">{valor}</div>
    {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
  </div>
);

const Barras = ({ itens, rotulo, valor, formato = (v) => v }) => {
  const max = Math.max(1, ...itens.map((i) => i[valor]));
  if (!itens.length) return <p className="text-sm text-slate-500">Sem dados no período.</p>;
  return (
    <ul className="space-y-2">
      {itens.map((i) => (
        <li key={i[rotulo]}>
          <div className="flex justify-between text-sm"><span className="text-slate-700">{i[rotulo]}</span><span className="font-semibold text-slate-900">{formato(i[valor])}</span></div>
          <div className="h-2 rounded-full bg-slate-100 mt-1"><div className="h-2 rounded-full bg-[#2A5BC7]" style={{ width: `${Math.max(3, (i[valor] / max) * 100)}%` }} /></div>
        </li>
      ))}
    </ul>
  );
};

const RelatoriosPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const qs = new URLSearchParams(useLocation().search);
  const [periodo, setPeriodo] = useState(qs.get('periodo') || '30');
  const [de, setDe] = useState(somaDias(hojeISO(), -29));
  const [ate, setAte] = useState(hojeISO());
  const [escopo, setEscopo] = useState(['todos', 'integracao'].includes(qs.get('escopo')) ? qs.get('escopo') : 'meus');
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buscaCliente, setBuscaCliente] = useState('');

  const intervalo = useMemo(() => {
    const h = hojeISO();
    if (periodo === 'mes') return { de: `${h.slice(0, 8)}01`, ate: h };
    if (periodo === 'custom') return { de, ate };
    return { de: somaDias(h, -(parseInt(periodo, 10) - 1)), ate: h };
  }, [periodo, de, ate]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setDados(await relatorioEnvios({ ...intervalo, escopo }));
    } catch (err) {
      if (err?.response?.status !== 401) toast({ title: 'Não foi possível gerar o relatório', description: err?.response?.data?.detail || 'Tente novamente.' });
    } finally { setLoading(false); }
  }, [intervalo, escopo, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  const exportar = () => {
    if (!dados) return;
    const adm = dados.escopo === 'todos';
    const integ = dados.escopo === 'integracao';
    const cab = ['Data', 'Hora', ...(adm ? ['Cliente'] : []), ...(integ ? ['Pedido no Gerenciador'] : []), 'Destinatário', 'Cidade', 'UF', 'Serviço', 'Peso (g)', 'Valor (R$)', 'Status', 'Código de rastreio', 'Cobrança'];
    const c = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const linhas = dados.envios.map((e) => [brData(e.data), e.hora, ...(adm ? [e.cliente] : []), ...(integ ? [e.pedido_gestao || ''] : []), e.destinatario, e.cidade, e.uf, e.servico, e.peso_g, String(e.valor).replace('.', ','), e.status, e.codigo_objeto, e.cobranca === 'carteira' ? 'Carteira' : 'Direta']);
    const csv = '﻿' + [cab, ...linhas].map((l) => l.map(c).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `Relatorio_envios_${dados.de}_a_${dados.ate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportarClientes = () => {
    if (!dados?.por_cliente?.length) return;
    const cab = ['Cliente', 'E-mail', 'Envios no período', 'Cancelados', 'Valor em fretes (R$)', 'Último envio', 'Saldo da carteira (R$)', 'Cadastro'];
    const c = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const linhas = dados.por_cliente.map((x) => [x.nome, x.email, x.envios, x.cancelados, String(x.valor).replace('.', ','), brData(x.ultimo_envio), String(x.saldo).replace('.', ','), brData(x.cadastro)]);
    const csv = '\uFEFF' + [cab, ...linhas].map((l) => l.map(c).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `Clientes_e_envios_${dados.de}_a_${dados.ate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const r = dados?.resumo;
  const adm = user?.is_admin;
  const clientesFiltrados = (dados?.por_cliente || []).filter((x) => {
    const q = buscaCliente.trim().toLowerCase();
    return !q || `${x.nome} ${x.email || ''}`.toLowerCase().includes(q);
  });

  return (
    <AppShell title="Relatórios">
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col">
              <Label htmlFor="periodo">Período</Label>
              <select id="periodo" value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="mt-1.5 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                {PERIODOS.map(([v, rot]) => <option key={v} value={v}>{rot}</option>)}
              </select>
            </div>
            {periodo === 'custom' && (
              <>
                <div className="flex flex-col"><Label htmlFor="de">De</Label><Input id="de" type="date" value={de} max={ate} onChange={(e) => setDe(e.target.value)} className="mt-1.5 h-10" /></div>
                <div className="flex flex-col"><Label htmlFor="ate">Até</Label><Input id="ate" type="date" value={ate} min={de} max={hojeISO()} onChange={(e) => setAte(e.target.value)} className="mt-1.5 h-10" /></div>
              </>
            )}
            {adm && (
              <div className="flex flex-col">
                <Label htmlFor="escopo">Mostrar</Label>
                <select id="escopo" value={escopo} onChange={(e) => setEscopo(e.target.value)} className="mt-1.5 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="meus">Só os meus envios</option>
                  <option value="todos">Todos os clientes (administrador)</option>
                  <option value="integracao">Envios da integração (Gerenciador)</option>
                </select>
              </div>
            )}
            <Button onClick={exportar} disabled={!dados || !dados.envios.length} variant="outline" className="ml-auto h-10"><Download className="w-4 h-4 mr-2" /> Baixar Excel (CSV)</Button>
          </div>
          <p className="text-xs text-slate-500 mt-3">Período: {brData(dados?.de || intervalo.de)} a {brData(dados?.ate || intervalo.ate)}. Envios cancelados ficam fora dos valores e totais.</p>
        </div>

        {loading && !dados ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#2A5BC7]" /></div>
        ) : r && (
          <div className={loading ? 'opacity-60 transition-opacity space-y-6' : 'space-y-6'}>
            <div className={`grid gap-4 grid-cols-2 ${adm && escopo === 'todos' ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
              <Cartao icon={Package} titulo="Envios" valor={r.envios} sub={r.cancelados ? `${r.cancelados} cancelado(s)` : 'nenhum cancelado'} />
              <Cartao icon={Wallet} titulo="Valor em fretes" valor={BRL(r.valor_total)} sub={`Ticket médio ${BRL(r.ticket_medio)}`} />
              <Cartao icon={Scale} titulo="Peso total" valor={`${String(r.peso_total_kg).replace('.', ',')} kg`} />
              <Cartao icon={TrendingUp} titulo="Envios por dia" valor={dados.por_dia.length ? (r.envios / dados.por_dia.length).toFixed(1).replace('.', ',') : '0'} sub="nos dias com envio" />
              {adm && escopo === 'todos' && <Cartao icon={TrendingUp} titulo="Lucro bruto (carteira)" valor={BRL(r.lucro_bruto)} sub={`Venda ${BRL(r.venda_carteira)} − custo ${BRL(r.custo_contrato)}`} />}
            </div>
            {r.truncado && <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">O período tem mais de 5.000 envios; mostramos os 5.000 mais recentes. Escolha um período menor para ver tudo.</p>}

            <div className="grid gap-6 lg:grid-cols-3">
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm"><h3 className="font-bold text-slate-900 mb-4">Por serviço</h3><Barras itens={dados.por_servico} rotulo="servico" valor="valor" formato={BRL} /></div>
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm"><h3 className="font-bold text-slate-900 mb-4">Por situação</h3><Barras itens={dados.por_status} rotulo="status" valor="envios" /></div>
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm"><h3 className="font-bold text-slate-900 mb-4">Principais destinos (UF)</h3><Barras itens={dados.por_uf} rotulo="uf" valor="envios" /></div>
            </div>

            {dados.escopo === 'todos' && (
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div>
                    <h3 className="font-bold text-slate-900">Clientes e quantidade de envios</h3>
                    <p className="text-xs text-slate-500">{dados.por_cliente.filter((x) => x.envios > 0).length} de {dados.por_cliente.length} clientes enviaram no período. Quem não enviou aparece no fim, para você acompanhar.</p>
                  </div>
                  <Input value={buscaCliente} onChange={(e) => setBuscaCliente(e.target.value)} placeholder="Buscar cliente..." className="h-9 w-56 ml-auto" />
                  <Button onClick={exportarClientes} variant="outline" className="h-9"><Download className="w-4 h-4 mr-2" /> CSV</Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-slate-500 border-b">
                      <th className="py-2">Cliente</th><th className="py-2 text-right">Envios</th><th className="py-2 text-right pl-4">Cancelados</th>
                      <th className="py-2 text-right pl-4">Valor em fretes</th><th className="py-2 pl-6">Último envio</th><th className="py-2 text-right pl-4">Saldo carteira</th><th className="py-2 pl-6">Cadastro</th>
                    </tr></thead>
                    <tbody>
                      {clientesFiltrados.map((x) => (
                        <tr key={`${x.email}-${x.nome}`} className={`border-b border-slate-50 ${x.envios ? '' : 'text-slate-400'}`}>
                          <td className="py-2"><div className="font-medium">{x.nome}</div><div className="text-xs text-slate-400">{x.email}</div></td>
                          <td className="py-2 text-right font-bold">{x.envios}</td>
                          <td className="py-2 text-right">{x.cancelados || '–'}</td>
                          <td className="py-2 text-right">{x.envios ? BRL(x.valor) : '–'}</td>
                          <td className="py-2 pl-6">{x.ultimo_envio ? brData(x.ultimo_envio) : 'nunca enviou'}</td>
                          <td className="py-2 text-right">{BRL(x.saldo)}</td>
                          <td className="py-2 pl-6">{brData(x.cadastro) || '–'}</td>
                        </tr>
                      ))}
                      {!clientesFiltrados.length && <tr><td colSpan="7" className="py-4 text-center text-slate-500">Nenhum cliente encontrado.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {dados.escopo === 'integracao' && (
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <h3 className="font-bold text-slate-900">Gastos com a integração — envio por envio</h3>
                <p className="text-xs text-slate-500 mb-4">Para conferir com o Gerenciador: cada etiqueta emitida pelo sistema de gestão, com o número do pedido de lá. Cancelados aparecem riscados e não somam no total ({BRL(r.valor_total)}).</p>
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">Data</th><th className="py-2">Pedido</th><th className="py-2">Destinatário</th><th className="py-2">Código</th><th className="py-2">Serviço</th><th className="py-2">Situação</th><th className="py-2 text-right">Valor</th></tr></thead>
                  <tbody>{dados.envios.map((e, i) => {
                    const canc = ['Cancelado', 'Cancelando'].includes(e.status);
                    return (
                      <tr key={`${e.codigo_objeto}-${i}`} className={`border-b border-slate-50 ${canc ? 'text-slate-400' : ''}`}>
                        <td className="py-2 whitespace-nowrap">{brData(e.data)}</td>
                        <td className="py-2 font-semibold">{e.pedido_gestao || '–'}</td>
                        <td className="py-2">{e.destinatario}<div className="text-xs text-slate-400">{[e.cidade, e.uf].filter(Boolean).join('/')}</div></td>
                        <td className="py-2 font-mono text-xs">{e.codigo_objeto || '–'}</td>
                        <td className="py-2">{e.servico}</td>
                        <td className="py-2">{e.status}</td>
                        <td className={`py-2 text-right font-semibold ${canc ? 'line-through' : ''}`}>{BRL(e.valor)}</td>
                      </tr>
                    );
                  })}</tbody>
                  <tfoot><tr className="border-t-2 border-slate-200"><td colSpan="6" className="py-2 font-bold">Total ({r.envios} envios, sem os cancelados)</td><td className="py-2 text-right font-extrabold">{BRL(r.valor_total)}</td></tr></tfoot>
                </table></div>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-bold text-slate-900">Destinatários que mais receberam</h3>
              <p className="text-xs text-slate-500 mb-4">Os {dados.por_destinatario.length} destinatários com mais envios no período{dados.escopo === 'todos' ? ' (de todos os clientes)' : ''}.</p>
              {dados.por_destinatario.length ? (
                <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-slate-500 border-b"><th className="py-2">Destinatário</th><th className="py-2">Cidade/UF</th><th className="py-2 text-right">Envios</th><th className="py-2 text-right">Valor</th></tr></thead>
                  <tbody>{dados.por_destinatario.map((d) => <tr key={`${d.destinatario}-${d.cidade}`} className="border-b border-slate-50"><td className="py-2 font-medium">{d.destinatario}</td><td className="py-2">{[d.cidade, d.uf].filter(Boolean).join('/')}</td><td className="py-2 text-right font-bold">{d.envios}</td><td className="py-2 text-right">{BRL(d.valor)}</td></tr>)}</tbody></table></div>
              ) : <p className="text-sm text-slate-500">Nenhum envio no período.</p>}
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-bold text-slate-900">Envios por dia</h3>
              <p className="text-xs text-slate-500 mb-4">{dados.escopo === 'todos' ? 'Em cada dia, quais clientes enviaram e quantos envios fizeram.' : 'Em cada dia, para quem você enviou e quantos envios.'}</p>
              {dados.por_dia_detalhe?.length ? (
                <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-slate-500 border-b"><th className="py-2 w-32">Data</th><th className="py-2">{dados.escopo === 'todos' ? 'Cliente' : 'Destinatário'}</th><th className="py-2 text-right">Envios</th><th className="py-2 text-right">Valor</th></tr></thead>
                  <tbody>{dados.por_dia_detalhe.map((d, i, arr) => {
                    const primeiro = i === 0 || arr[i - 1].dia !== d.dia;
                    return (
                      <tr key={`${d.dia}-${d.nome}`} className={`border-b border-slate-50 ${primeiro && i > 0 ? 'border-t border-t-slate-200' : ''}`}>
                        <td className="py-2 font-semibold text-slate-800">{primeiro ? brData(d.dia) : ''}</td>
                        <td className="py-2">{d.nome}</td><td className="py-2 text-right font-bold">{d.envios}</td><td className="py-2 text-right">{BRL(d.valor)}</td>
                      </tr>
                    );
                  })}</tbody></table></div>
              ) : <p className="text-sm text-slate-500">Nenhum envio no período.</p>}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default RelatoriosPage;
